import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import TenantSwitcherSelect from '@devnw/cyber-iam/identity/TenantSwitcherSelect/TenantSwitcherSelect'
import type { TenantOption } from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useAuth } from '../auth/AuthContext'
import {
  readPersistedActiveTenant,
  writePersistedActiveTenant,
} from '../auth/activeTenantStorage'

/**
 * Explicit tenant-resolution state, so "in flight" vs "exhausted" is never
 * inferred from `tenant === '' && tenantError === null` at call sites (that
 * inference is exactly what let Finding 1 / Finding 2 of the 2026-07-24 fix
 * wave slip through: an empty-but-successful adminListTenants() response,
 * and a non-admin caller for whom no attempt was ever made, both looked
 * identical to "still loading").
 *
 *   idle       — mounted, but the caller's identity (claims) isn't known yet.
 *   resolving  — identity is known; a resolution attempt is genuinely
 *                possible/outstanding (e.g. refreshTenants() in flight).
 *   resolved   — `tenant` is non-empty.
 *   unavailable — resolution is EXHAUSTED with no usable tenant: `tenant`
 *                stays '' and `tenantError` carries an accessible message.
 *
 * TenantGate treats idle/resolving identically (spinner) and unavailable as
 * terminal (accessible alert) — see TenantGate.tsx.
 */
export type TenantResolutionStatus =
  | 'idle'
  | 'resolving'
  | 'resolved'
  | 'unavailable'

export interface TenantContextValue {
  tenant: string
  setTenant(id: string): void
  tenants: string[]
  isSystemAdmin: boolean
  refreshTenants(): Promise<void>
  /**
   * Set when tenant resolution is EXHAUSTED with no usable tenant. Non-null
   * means the caller-visible tenant gate (AdminLayout / BareLayout) must
   * stop spinning and show an accessible error instead — an unbounded
   * spinner is never acceptable. Cleared automatically once a tenant
   * resolves by any path. See `resolutionStatus` for the explicit state
   * this is derived from.
   */
  tenantError: string | null
  /** Explicit resolution state — see `TenantResolutionStatus` above. */
  resolutionStatus: TenantResolutionStatus
}

const TenantContext = createContext<TenantContextValue | null>(null)

/** Synchronously derive the tenant from claims available on first render. */
function tenantFromClaims(claims: Record<string, unknown> | null): string {
  if (claims === null) return ''
  const t = claims.tenant
  const tid = claims.tid
  if (typeof t === 'string') return t
  if (typeof tid === 'string') return tid
  return ''
}

/** Extract `sub` from claims, normalized to `string | null`. */
function subFromClaims(claims: Record<string, unknown> | null): string | null {
  if (claims === null) return null
  const sub = claims.sub
  return typeof sub === 'string' ? sub : null
}

export const TenantProvider = ({ children }: { children: React.ReactNode }) => {
  const { claims, token } = useAuth()
  // Task 17a: tenant list served by the genapi-generated client.
  const client = useAdminGenClient()

  const isSystemAdmin = (claims?.roles ?? []).includes('admin')

  // Resolve the active tenant SYNCHRONOUSLY on first render (lazy
  // initializer) so child pages never mount + fetch with an empty tenantId.
  // Priority:
  //   1. for a system admin ONLY: a previously persisted selection that was
  //      resolved for THIS SAME subject (survives refresh) — see
  //      readPersistedActiveTenant's subject check (Finding 3), else
  //   2. the claims-derived tenant (tenant/tid claim) — the ONLY signal for a
  //      non-system-admin (their claim IS their real, pinned tenant), and
  //      the fallback for a system admin with no persisted selection yet
  //      (this is what fixed the original perpetual-spinner bug — Task 7),
  //      else
  //   3. '' — only for a system admin whose claims carry no tenant and who
  //      has no persisted selection for their own subject; resolved async by
  //      refreshTenants() below. A non-admin with no tenant claim NEVER
  //      falls through to a persisted value (Finding 2/3) — the main
  //      resolution effect below marks them exhausted instead.
  //
  // IMPORTANT-4 (fix-wave 2026-07-24): persisted MUST win over claims for a
  // system admin — every admin access token carries a `tenant` claim once
  // issued (THREADID_TENANT_ID defaults to "default"; jwt.go always sets it
  // when tenantID != ""), so "claims always wins" silently overrode a system
  // admin's actual switcher selection with the server default on every
  // refresh. Safe now that persistence is subject-scoped (df742ee2) — it can
  // never leak across users, so restoring this precedence does not reopen
  // any of that commit's three fixed Criticals (empty-list handling,
  // non-admin short-circuit, and cross-user leakage are all untouched below).
  const [tenant, setTenantState] = useState<string>(() => {
    if (isSystemAdmin) {
      const persisted = readPersistedActiveTenant(subFromClaims(claims ?? null))
      if (persisted) return persisted
    }
    return tenantFromClaims(claims ?? null)
  })
  const [tenants, setTenants] = useState<string[]>([])
  const [tenantError, setTenantError] = useState<string | null>(null)
  // refreshTenants awaits a network call and then setState()s. If the provider
  // unmounts before the response lands (e.g. a test tears down its render),
  // those updates fire outside act() and — under jest's parallel workers, which
  // share a module registry — can leak into whichever test is running, failing
  // it intermittently (observed: AccessRequestDetail.activate flaking on
  // setTenantError). Gate the post-await updates on this mounted ref.
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )

  // Persist the active tenant whenever it resolves/changes, scoped to the
  // CURRENT subject, so the next refresh restores it synchronously — but
  // only for that same subject (Finding 3: a persisted value must never be
  // adoptable by a different user). Only persist non-empty values (clearing
  // on empty would drop a still-valid selection while the list is being
  // refetched).
  useEffect(() => {
    if (!tenant) return
    writePersistedActiveTenant(subFromClaims(claims ?? null), tenant)
  }, [tenant, claims])

  // Single source of truth for LOCAL (non-network) tenant resolution: runs
  // whenever the caller's identity changes. Network resolution
  // (refreshTenants, system-admin only) is a separate effect below.
  //
  //  - A system admin's persisted selection (IF resolved for this same
  //    subject) wins over their claims tenant (IMPORTANT-4) — falling back
  //    to the claims tenant only when nothing is persisted yet. This is what
  //    lets a system admin's switcher selection survive a refresh instead of
  //    being clobbered by the claim's server-default tenant every time.
  //  - A non-system-admin's claims-derived tenant ALWAYS wins (Task 7: this
  //    was previously gated on `!isSystemAdmin`, which was DEAD CODE inside
  //    /admin since ProtectedRoute only admits roles.includes('admin')) —
  //    they never fall back to a persisted value under any circumstance
  //    (Finding 2/3).
  //  - A non-system-admin with NO tenant claim has no list-tenants call
  //    available (system-admin-only) — resolution is marked EXHAUSTED
  //    immediately rather than either spinning forever (Finding 2) or
  //    silently keeping a stale/previous tenant. `claims === null` means
  //    identity itself isn't known yet (e.g. TenantProvider mounted before
  //    AuthContext's rehydration/callback exchange resolved) — stay idle and
  //    wait.
  useEffect(() => {
    const claimsTenant = tenantFromClaims(claims ?? null)

    if (isSystemAdmin) {
      const persisted = readPersistedActiveTenant(subFromClaims(claims ?? null))
      if (persisted) {
        setTenantState(persisted)
        setTenantError(null)
        return
      }
      if (claimsTenant) {
        setTenantState(claimsTenant)
        setTenantError(null)
      }
      // else: leave as-is — refreshTenants() below is the only remaining
      // resolution path and will either resolve it or mark it exhausted.
      return
    }

    if (claimsTenant) {
      setTenantState(claimsTenant)
      setTenantError(null)
      return
    }

    if (claims !== null) {
      setTenantState('')
      setTenantError('Your account has no tenant assigned.')
    }
  }, [claims, isSystemAdmin])

  // Fetch the tenant list from the generated client. System-admin only —
  // adminListTenants is not available to a non-admin caller at all (see the
  // main resolution effect above, which marks non-admins exhausted locally
  // instead of attempting this). Extracted into a useCallback so it can be
  // re-run on demand (e.g. after a tenant is created on the Tenants page) —
  // not just once on mount.
  const refreshTenants = useCallback(async (): Promise<void> => {
    if (!isSystemAdmin || !token) return
    try {
      const resp = await client.adminListTenants({})
      // Unmounted while the request was in flight — do not setState (see
      // mountedRef above): a post-unmount update here is the flake source.
      if (!mountedRef.current) return
      const ids = (resp.tenants ?? [])
        .map((t) => (t as { id?: string }).id ?? '')
        .filter((id) => id !== '')
      setTenants(ids)
      setTenantState((cur) => {
        if (!cur || !ids.includes(cur)) return ids[0] ?? ''
        return cur
      })
      // A SUCCESSFUL response that resolves to NOTHING usable is EXHAUSTED,
      // not in-flight (Finding 1) — e.g. a first-run system with no tenants
      // yet, or an admin assigned to zero tenants. `ids.length === 0`
      // deterministically means the tenant above resolved to '' (the
      // functional updater above can only return a non-empty value when
      // `ids` is non-empty), so this check is race-free regardless of what
      // `cur` was.
      if (ids.length === 0) {
        setTenantError('No tenants exist for this account yet.')
      } else {
        setTenantError(null)
      }
    } catch (err) {
      // Same unmount guard as the success path — a rejected in-flight request
      // must not setState after the provider has been torn down.
      if (!mountedRef.current) return
      // Do NOT swallow: a system admin (or scoped admin) whose claims carry
      // NO tenant at all has no other resolution path left once this fails.
      // Silently degrading here is exactly what previously wedged the whole
      // admin shell behind AdminLayout's `tenant === ''` gate with no way
      // out. (A caller who already resolved a tenant via claims is
      // unaffected: AdminLayout/BareLayout only render the gate at all while
      // `tenant === ''`, so a background failure here can never clobber an
      // already-visible page — see AdminLayout.test.tsx's Task 7 "scoped
      // admin, 403" regression test.)
      setTenantError(
        err instanceof Error && 'status' in err && (err as { status?: number }).status === 403
          ? 'Your account cannot list tenants.'
          : 'Could not load the tenant list.'
      )
    }
  }, [isSystemAdmin, token, client])

  useEffect(() => {
    refreshTenants()
  }, [refreshTenants])

  const setTenant = useCallback(
    (id: string): void => {
      if (isSystemAdmin) {
        setTenantState(id)
      }
    },
    [isSystemAdmin]
  )

  const resolutionStatus: TenantResolutionStatus = useMemo(() => {
    if (tenant !== '') return 'resolved'
    if (tenantError !== null) return 'unavailable'
    return claims === null ? 'idle' : 'resolving'
  }, [tenant, tenantError, claims])

  const value = useMemo(
    (): TenantContextValue => ({
      tenant,
      setTenant,
      tenants,
      isSystemAdmin,
      refreshTenants,
      tenantError,
      resolutionStatus,
    }),
    [
      tenant,
      setTenant,
      tenants,
      isSystemAdmin,
      refreshTenants,
      tenantError,
      resolutionStatus,
    ]
  )

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  )
}

export const useActiveTenant = (): TenantContextValue => {
  const ctx = useContext(TenantContext)
  if (!ctx) {
    throw new Error('useActiveTenant must be used within <TenantProvider>')
  }
  return ctx
}

export const TenantSwitcher = () => {
  const { tenant, setTenant, tenants, isSystemAdmin, refreshTenants } =
    useActiveTenant()

  // A system admin sees the switcher as soon as there is at least one tenant,
  // so adding a second tenant makes it live-switchable without a reload.
  // readOnly mirrors the previous MUI gate exactly: editable select only for
  // a system admin who has at least one tenant resolved; otherwise a static
  // label (TenantSwitcherSelect's readOnly branch).
  const editable = isSystemAdmin && tenants.length >= 1
  const tenantOptions: TenantOption[] = tenants.map((id) => ({ id }))
  // Guard against an out-of-range value only in the editable branch (mirrors
  // the previous MUI Select's displayEmpty guard); the read-only branch shows
  // the raw active tenant regardless of whether the list has resolved yet.
  let selectValue = tenant
  if (editable && !tenants.includes(tenant)) {
    selectValue = ''
  }

  return (
    <TenantSwitcherSelect
      label="Active tenant"
      tenants={tenantOptions}
      value={selectValue}
      onChange={setTenant}
      onOpen={() => {
        refreshTenants()
      }}
      readOnly={!editable}
      testID="tenant-switcher"
    />
  )
}
